import { usePaymentState } from 'payment_state/PaymentStateContext';
import React, { useEffect, useMemo, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useNavigate } from 'react-router-dom';

/** 보안상 허용할 부모 오리진 화이트리스트(선택) */
const ALLOWED_PARENT_ORIGINS = [
  `${window.location.origin}`,
  // 필요 시 추가
] as const;

/** base64url → JSON 파싱(옵션: state에 parentOrigin을 넣어 보냈다면 사용) */
function parseBase64UrlJson<T = any>(input?: string | null): T | null {
  if (!input) return null;
  try {
    // base64url → base64 변환
    const b64 = input.replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(b64);
    return JSON.parse(json) as T;
  } catch {
    return null;
  }
}

/** search + hash 둘 다에서 파라미터 수집 */
function collectParams(): URLSearchParams {
  const search = new URLSearchParams(window.location.search);
  const hash = window.location.hash?.startsWith('#')
    ? new URLSearchParams(window.location.hash.slice(1))
    : new URLSearchParams();

  // hash에 있는 키가 있다면 search보다 우선(필요 시 반대로 변경)
  hash.forEach((v, k) => search.set(k, v));
  return search;
}

type BridgeMessage = {
  type: 'POPUP_REDIRECT';
  url: string;
  payload: Record<string, string | null>;
};

const ReturnBridgePage: React.FC = () => {
  const [error, setError] = useState<string | null>(null);
  const { paymentState, setPaymentState } = usePaymentState();
  const naviate = useNavigate();
  const { parentOrigin, payload, href } = useMemo(() => {
    const params = collectParams();
    const href = window.location.href;

    // 1) redirect_uri에 parentOrigin을 그대로 붙여 넣은 경우
    let parentOrigin = params.get('parentOrigin');

    // 2) state(JSON을 base64url로 인코딩) 안에 넣어 전달한 경우
    //    예: state={"nonce":"...","parentOrigin":"https://parent.example.com"}
    if (!parentOrigin) {
      const stateRaw = params.get('state');
      const stateDecoded = parseBase64UrlJson<{ parentOrigin?: string }>(
        stateRaw,
      );
      if (stateDecoded?.parentOrigin) parentOrigin = stateDecoded.parentOrigin;
    }

    // payload는 쿼리 전체를 전달하되, 민감/내부 키는 제외
    const payload: Record<string, string | null> = {};
    params.forEach((value, key) => {
      if (key === 'parentOrigin') return; // 내부용
      payload[key] = value;
    });

    return { parentOrigin, payload, href };
  }, []);

  useEffect(() => {
    if (isMobile) {
      const response = JSON.parse(String(payload.state));
      setPaymentState(prev => ({
        ...prev,
        resultCode: response.resultCode,
        resultStatus: response.resultStatus,
        resultMessage: response.resultMessage,
        orderId: response.orderId,
        mallReserved: response.mallReserved,
        tid: response.tid,
        returnUrl: response.returnUrl,
      }));
      naviate('/error');
    } else {
      try {
        if (!window.opener) {
          setError(
            "opener가 없습니다. 팝업이 아닌 창으로 열렸거나 'noopener'가 설정되었을 수 있어요.",
          );
          return;
        }
        if (!parentOrigin) {
          setError(
            'parentOrigin을 찾지 못했습니다. redirect_uri에 parentOrigin을 포함하거나 state에 넣어 전달하세요.',
          );
          return;
        }

        // 화이트리스트 검증(선택)
        const isAllowed = ALLOWED_PARENT_ORIGINS.includes(parentOrigin as any);
        if (!isAllowed) {
          setError(`허용되지 않은 parentOrigin입니다: ${parentOrigin}`);
          return;
        }

        const message: BridgeMessage = {
          type: 'POPUP_REDIRECT',
          url: href,
          payload,
        };

        // 부모에게 안전하게 전달
        window.opener.postMessage(message, parentOrigin);

        // 바로 닫기(원치 않으면 주석 처리)
        window.close?.();
      } catch (e) {
        setError('부모 창으로 메시지 전달 중 오류가 발생했습니다.');
      }
    }
  }, [href, parentOrigin, payload]);

  // 실패/디버그 시 화면에 간단 표출(성공하면 곧 닫힘)
  if (error) {
    return (
      <main style={{ padding: 24, fontFamily: 'system-ui, sans-serif' }}>
        <h1>Return Bridge</h1>
        <p style={{ color: 'crimson' }}>{error}</p>
        <details>
          <summary>payload 보기</summary>
          <pre>{JSON.stringify(payload, null, 2)}</pre>
        </details>
      </main>
    );
  }
  return null;
};

export default ReturnBridgePage;
